/**
 * SizilPro — useBrandSettings
 * TanStack Query hooks for brand_settings table operations.
 *
 * Updated for builder-foundation direction:
 * - richer brand_settings support
 * - brand_assets
 * - brand_fonts
 * - safer setup save/load behavior
 *
 * File: src/modules/proposal-builder/hooks/useBrandSettings.ts
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Json } from "@/lib/database.types";
import { useAuthStore } from "@/store/authStore";
import type {
  BrandAsset,
  BrandAssetInsert,
  BrandFont,
  BrandFontInsert,
  BrandSettings,
  BrandSettingsUpdate,
  BrandPreviewPreferences,
} from "../types";

const brandSettingsKey = (clientId: string | null) =>
  ["brand-settings", clientId ?? "no-client"] as const;

const brandAssetsKey = (clientId: string | null) =>
  ["brand-assets", clientId ?? "no-client"] as const;

const brandFontsKey = (clientId: string | null) =>
  ["brand-fonts", clientId ?? "no-client"] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function toJsonValue(value: unknown): Json {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => toJsonValue(item));
  }

  if (isRecord(value)) {
    const output: { [key: string]: Json | undefined } = {};
    for (const [key, entry] of Object.entries(value)) {
      output[key] = toJsonValue(entry);
    }
    return output;
  }

  return null;
}

function toJsonRecord(value: unknown): Record<string, Json> {
  if (!isRecord(value)) {
    return {};
  }

  const output: Record<string, Json> = {};
  for (const [key, entry] of Object.entries(value)) {
    output[key] = toJsonValue(entry);
  }
  return output;
}

function toPreviewPreferences(value: unknown): BrandPreviewPreferences {
  return toJsonRecord(value) as BrandPreviewPreferences;
}

function normalizeBrandSettings(row: Record<string, unknown>): BrandSettings {
  const normalized = row as unknown as BrandSettings;

  return {
    ...normalized,
    color_palette: toJsonRecord(row.color_palette),
    preview_preferences: toPreviewPreferences(row.preview_preferences),
    proposal_image_rules: toJsonRecord(row.proposal_image_rules),
  };
}

// ─── Main hook ────────────────────────────────────────────────
export function useBrandSettings() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  const query = useQuery<BrandSettings | null>({
    queryKey: brandSettingsKey(clientId),
    queryFn: async () => {
      if (!clientId) return null;

      const { data, error } = await supabase
        .from("brand_settings")
        .select("*")
        .eq("client_id", clientId)
        .maybeSingle();

      if (error) throw error;
      if (!data) return null;

      return normalizeBrandSettings(data as Record<string, unknown>);
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: BrandSettingsUpdate) => {
      if (!clientId) throw new Error("No client ID");

      const payload = {
        ...updates,
        client_id: clientId,
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from("brand_settings")
        .upsert(payload, { onConflict: "client_id" })
        .select()
        .single();

      if (error) throw error;

      return normalizeBrandSettings(data as Record<string, unknown>);
    },
    onSuccess: (data) => {
      qc.setQueryData(brandSettingsKey(clientId), data);
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandAssetsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandFontsKey(clientId) });
    },
  });

  return {
    ...query,
    updateBrandSettings: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}

// ─── Backward-compatible upsert hook ──────────────────────────
export function useUpsertBrandSettings() {
  const { updateBrandSettings, isUpdating } = useBrandSettings();

  return {
    mutateAsync: updateBrandSettings,
    isPending: isUpdating,
  };
}

// ─── Brand assets ─────────────────────────────────────────────

export function useBrandAssets() {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<BrandAsset[]>({
    queryKey: brandAssetsKey(clientId),
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from("brand_assets")
        .select("*")
        .eq("client_id", clientId)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as BrandAsset[];
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

export function useCreateBrandAsset() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: BrandAssetInsert) => {
      if (!clientId) throw new Error("No client ID");

      const { data, error } = await supabase
        .from("brand_assets")
        .insert({
          ...input,
          client_id: clientId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as BrandAsset;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandAssetsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

export function useUpdateBrandAsset() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<BrandAssetInsert>;
    }) => {
      if (!clientId) throw new Error("No client ID");

      const { data, error } = await supabase
        .from("brand_assets")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as BrandAsset;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandAssetsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

export function useDeleteBrandAsset() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!clientId) throw new Error("No client ID");

      const { error } = await supabase
        .from("brand_assets")
        .delete()
        .eq("id", id)
        .eq("client_id", clientId);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandAssetsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

// ─── Brand fonts ──────────────────────────────────────────────

export function useBrandFonts() {
  const clientId = useAuthStore((s) => s.clientId);

  return useQuery<BrandFont[]>({
    queryKey: brandFontsKey(clientId),
    queryFn: async () => {
      if (!clientId) return [];

      const { data, error } = await supabase
        .from("brand_fonts")
        .select("*")
        .eq("client_id", clientId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as BrandFont[];
    },
    enabled: !!clientId,
    staleTime: 1000 * 30,
    refetchOnMount: true,
  });
}

export function useCreateBrandFont() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: BrandFontInsert) => {
      if (!clientId) throw new Error("No client ID");

      if (input.is_default) {
        const { error: clearDefaultError } = await supabase
          .from("brand_fonts")
          .update({ is_default: false, updated_at: new Date().toISOString() })
          .eq("client_id", clientId)
          .eq("is_default", true);

        if (clearDefaultError) throw clearDefaultError;
      }

      const { data, error } = await supabase
        .from("brand_fonts")
        .insert({
          ...input,
          client_id: clientId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as BrandFont;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandFontsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

export function useUpdateBrandFont() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<BrandFontInsert>;
    }) => {
      if (!clientId) throw new Error("No client ID");

      if (updates.is_default) {
        const { error: clearDefaultError } = await supabase
          .from("brand_fonts")
          .update({ is_default: false, updated_at: new Date().toISOString() })
          .eq("client_id", clientId)
          .eq("is_default", true);

        if (clearDefaultError) throw clearDefaultError;
      }

      const { data, error } = await supabase
        .from("brand_fonts")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .eq("client_id", clientId)
        .select()
        .single();

      if (error) throw error;
      return data as BrandFont;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandFontsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

export function useDeleteBrandFont() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!clientId) throw new Error("No client ID");

      const { error } = await supabase
        .from("brand_fonts")
        .delete()
        .eq("id", id)
        .eq("client_id", clientId);

      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandFontsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

// ─── Logo upload ──────────────────────────────────────────────
export function useUploadLogo() {
  const clientId = useAuthStore((s) => s.clientId);
  const { updateBrandSettings } = useBrandSettings();

  return useMutation({
    mutationFn: async (file: File) => {
      if (!clientId) throw new Error("No client ID");

      const ext = file.name.split(".").pop() ?? "png";
      const originalPath = `${clientId}/logo-original.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from("proposal-assets")
        .upload(originalPath, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("proposal-assets")
        .getPublicUrl(originalPath);

      const publicUrl = urlData.publicUrl;

      await updateBrandSettings({
        logo_url: publicUrl,
        logo_original_url: publicUrl,
        show_logo: true,
      });

      return publicUrl;
    },
  });
}

// ─── Generic asset upload ─────────────────────────────────────

export function useUploadBrandAsset() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      asset,
      setAsDefaultLogo = false,
    }: {
      file: File;
      asset: Omit<
        BrandAssetInsert,
        "storage_path" | "public_url" | "mime_type" | "file_size_bytes"
      >;
      setAsDefaultLogo?: boolean;
    }) => {
      if (!clientId) throw new Error("No client ID");

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
      const filePath = `${clientId}/brand-assets/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("proposal-assets")
        .upload(filePath, file, {
          upsert: false,
          contentType: file.type,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("proposal-assets")
        .getPublicUrl(filePath);

      const { data, error } = await supabase
        .from("brand_assets")
        .insert({
          ...asset,
          client_id: clientId,
          storage_path: filePath,
          public_url: urlData.publicUrl,
          mime_type: file.type,
          file_size_bytes: file.size,
        })
        .select()
        .single();

      if (error) throw error;

      const createdAsset = data as BrandAsset;

      if (setAsDefaultLogo) {
        const { error: defaultLogoError } = await supabase
          .from("brand_settings")
          .upsert(
            {
              client_id: clientId,
              default_logo_asset_id: createdAsset.id,
              logo_url: createdAsset.public_url,
              logo_original_url: createdAsset.public_url,
              show_logo: true,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "client_id" }
          );

        if (defaultLogoError) throw defaultLogoError;
      }

      return createdAsset;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandAssetsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

// ─── Font upload ──────────────────────────────────────────────

export function useUploadBrandFont() {
  const clientId = useAuthStore((s) => s.clientId);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      font,
      setAsDefault = false,
    }: {
      file: File;
      font: Omit<
        BrandFontInsert,
        "storage_path" | "public_url" | "file_format" | "source_type"
      >;
      setAsDefault?: boolean;
    }) => {
      if (!clientId) throw new Error("No client ID");

      const ext = (file.name.split(".").pop() ?? "").toLowerCase();
      if (!["ttf", "otf", "woff", "woff2"].includes(ext)) {
        throw new Error(
          "Unsupported font format. Use .ttf, .otf, .woff, or .woff2"
        );
      }

      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
      const filePath = `${clientId}/fonts/${Date.now()}-${safeName}`;

      const { error: uploadError } = await supabase.storage
        .from("proposal-assets")
        .upload(filePath, file, {
          upsert: false,
          contentType: file.type || "font/ttf",
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("proposal-assets")
        .getPublicUrl(filePath);

      if (setAsDefault) {
        const { error: clearDefaultError } = await supabase
          .from("brand_fonts")
          .update({ is_default: false, updated_at: new Date().toISOString() })
          .eq("client_id", clientId)
          .eq("is_default", true);

        if (clearDefaultError) throw clearDefaultError;
      }

      const { data, error } = await supabase
        .from("brand_fonts")
        .insert({
          ...font,
          client_id: clientId,
          source_type: "uploaded",
          storage_path: filePath,
          public_url: urlData.publicUrl,
          file_format: ext,
          is_default: setAsDefault,
        })
        .select()
        .single();

      if (error) throw error;

      const createdFont = data as BrandFont;

      if (setAsDefault) {
        const { error: defaultFontError } = await supabase
          .from("brand_settings")
          .upsert(
            {
              client_id: clientId,
              default_font_id: createdFont.id,
              updated_at: new Date().toISOString(),
            },
            { onConflict: "client_id" }
          );

        if (defaultFontError) throw defaultFontError;
      }

      return createdFont;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: brandFontsKey(clientId) });
      qc.invalidateQueries({ queryKey: brandSettingsKey(clientId) });
    },
  });
}

// ─── Mark setup complete ──────────────────────────────────────
export function useMarkSetupComplete() {
  const { updateBrandSettings } = useBrandSettings();

  return async () => {
    await updateBrandSettings({ setup_complete: true });
  };
}